import { BASIC_LIMITS, isWork } from "../../pages/pageMetadata";
import type { EditorPage } from "../editor.types";
import { useState } from "react";
import { Check, ExternalLink, Globe, LogOut } from "lucide-react";
import { config } from "../../../lib/config";
import { SUPPORT_EMAIL } from "../../../lib/contact";
import { normalizeSubdomainInput } from "../../../lib/hostname";
import {
  Button,
  Card,
  FieldError,
  Hint,
  Input,
  Notice,
  SectionHeading,
  textLinkClass,
} from "../../../components/ui";
import { authService } from "../../auth";
import { BottomSheet } from "./BottomSheet";
import { DeleteAccountSection } from "./DeleteAccountSection";
import { DomainsSection } from "./DomainsSection";
import { SubscriptionSection } from "./SubscriptionSection";

type YouSheetProps = {
  open: boolean;
  inline?: boolean;
  siteId: string;
  siteName?: string;
  pages?: EditorPage[];
  onSaveName: (name: string) => Promise<string | null>;
  siteSubdomain: string | null;
  isPublished: boolean;
  onSaveSubdomain: (value: string | null) => Promise<string | null>;
  isSubscribed: boolean;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  subscribing: boolean;
  managing: boolean;
  subscriptionError: string;
  onClose: () => void;
  onSubscribe: () => void;
  onManage: () => void;
};

export const YouSheet = ({
  open,
  inline = false,
  siteId,
  siteName,
  pages = [],
  onSaveName,
  siteSubdomain,
  isPublished,
  onSaveSubdomain,
  isSubscribed,
  currentPeriodEnd,
  cancelAtPeriodEnd,
  subscribing,
  managing,
  subscriptionError,
  onClose,
  onSubscribe,
  onManage,
}: YouSheetProps) => {
  const [address, setAddress] = useState(siteSubdomain ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setAddress(siteSubdomain ?? "");
      setError("");
      setSaved(false);
    }
  }

  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");

  const signOut = async () => {
    setSigningOut(true);
    setSignOutError("");
    const message = await authService.signOut();
    if (message) {
      setSignOutError(message);
      setSigningOut(false);
    }
  };

  const dirty = address.trim() !== (siteSubdomain ?? "");
  const liveHost = siteSubdomain
    ? `${siteSubdomain}.${config.hazumuRootDomain}`
    : null;

  // On a live site, changing or clearing the address breaks the old links,
  // so ask first.
  const [confirming, setConfirming] = useState<"clear" | "rename" | null>(null);
  const requestSave = () => {
    const next = normalizeSubdomainInput(address, config.hazumuRootDomain);
    if (isPublished && siteSubdomain && next !== siteSubdomain) {
      setConfirming(next ? "rename" : "clear");
      return;
    }
    void save();
  };

  const save = async () => {
    setConfirming(null);
    setSaving(true);
    setError("");
    setSaved(false);
    const message = await onSaveSubdomain(address.trim() || null);
    setSaving(false);
    if (message) {
      setError(message);
    } else {
      setSaved(true);
      setAddress(normalizeSubdomainInput(address, config.hazumuRootDomain));
    }
  };

  return (
    <>
    <BottomSheet
      inline={inline}
      disabled={saving}
      onClose={onClose}
      open={open}
      title="You"
      titleId="you-sheet-title"
    >
      <div>
        {inline && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4 lg:border-b lg:border-hazumu-border lg:pb-5">
            <p className="font-lora text-xl font-semibold">{siteName}</p>
            <span className="text-xs text-hazumu-muted">{isPublished ? "Published" : "Draft"}</span>
          </div>
        )}
        <Card className="my-5">
          <SectionHeading>Usage</SectionHeading>
          <div className="space-y-5">
            <UsageBar label="Website pages" used={pages.filter((p) => !isWork(p)).length} limit={BASIC_LIMITS.pages} />
            <UsageBar label="Work items" used={pages.filter(isWork).length} limit={BASIC_LIMITS.work} />
          </div>
          <Hint className="mt-4">
            Work items count once, wherever you showcase them.
          </Hint>
        </Card>
        <SiteNameForm
          key={`${open}-${siteName}`}
          name={siteName ?? ""}
          onSave={onSaveName}
        />
        <div className="my-5 border-t border-hazumu-border" />
        <section>
          <SectionHeading>Site address</SectionHeading>
          <div className={`flex items-center gap-2 lg:flex-wrap ${inline ? "flex-wrap" : ""}`}>
            <Globe aria-hidden className="h-4 w-4 shrink-0 text-hazumu-muted" />
            <Input
              aria-label="Site subdomain"
              autoCapitalize="none"
              autoCorrect="off"
              className={`lg:w-[calc(100%-26px)] ${inline ? "max-lg:w-auto max-lg:min-w-[120px] max-lg:flex-1" : ""}`}
              disabled={saving}
              onChange={(event) => {
                setAddress(event.target.value);
                setSaved(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && dirty) requestSave();
              }}
              placeholder="my-site"
              spellCheck={false}
              type="text"
              value={address}
            />
            <span className="shrink-0 text-sm text-hazumu-muted lg:ml-6">
              .{config.hazumuRootDomain}
            </span>
          </div>
          <Hint className="mt-2">
            Your free Hazumu address — lowercase letters, numbers and hyphens.
            {isPublished
              ? siteSubdomain
                ? ""
                : " Your site is published but has no address yet."
              : " Publish your site to put it live."}
          </Hint>
          {isPublished && liveHost && (
            <a
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-hazumu-terracotta transition hover:text-hazumu-ink"
              href={`https://${liveHost}`}
              rel="noreferrer"
              target="_blank"
            >
              {liveHost}
              <ExternalLink size={12} />
            </a>
          )}
          <div className="mt-3 flex items-center gap-3">
            <Button
              disabled={saving || !dirty}
              onClick={requestSave}
              size="sm"
              variant="secondary"
            >
              <Check size={14} />
              {saving ? "Saving…" : "Save"}
            </Button>
            {saved && (
              <span className="text-xs font-semibold text-hazumu-sage-deep">
                Saved
              </span>
            )}
          </div>
          {error && <Notice className="mt-3">{error}</Notice>}
        </section>

        <div className="my-5 border-t border-hazumu-border" />

        <section>
          <SectionHeading>Custom domain</SectionHeading>
          <DomainsSection
            isPublished={isPublished}
            isSubscribed={isSubscribed}
            onSubscribe={onSubscribe}
            siteId={siteId}
            siteSubdomain={siteSubdomain}
            subscribing={subscribing}
          />
        </section>

        <div className="my-5 border-t border-hazumu-border" />

        <section>
          <SectionHeading>Subscription</SectionHeading>
          <SubscriptionSection
            cancelAtPeriodEnd={cancelAtPeriodEnd}
            currentPeriodEnd={currentPeriodEnd}
            error={subscriptionError}
            isSubscribed={isSubscribed}
            managing={managing}
            onManage={onManage}
            onSubscribe={onSubscribe}
            subscribing={subscribing}
          />
        </section>

        <div className="my-5 border-t border-hazumu-border" />

        <section>
          <SectionHeading>Get in touch</SectionHeading>
          <Hint>
            Questions, problems or ideas? Email{" "}
            <a
              className={textLinkClass}
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </Hint>
        </section>

        <div className="my-5 border-t border-hazumu-border" />

        <section>
          <Button
            disabled={signingOut}
            onClick={() => void signOut()}
            size="sm"
            variant="secondary"
          >
            <LogOut size={14} />
            {signingOut ? "Signing out…" : "Log out"}
          </Button>
          {signOutError && <Notice className="mt-3">{signOutError}</Notice>}
        </section>

        <div className="my-5 border-t border-hazumu-border" />

        <DeleteAccountSection isSubscribed={isSubscribed} />
      </div>
    </BottomSheet>
    {confirming && (
      <BottomSheet
        open
        disabled={saving}
        title={confirming === "clear" ? "Remove your address?" : "Change your address?"}
        titleId="address-change-title"
        onClose={() => setConfirming(null)}
      >
        <p className="mb-4 text-sm text-hazumu-muted">
          {confirming === "clear"
            ? "Your site will go offline until you add an address."
            : `Links to ${liveHost} will stop working.`}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button disabled={saving} onClick={() => void save()} variant="danger">
            {confirming === "clear" ? "Remove address" : "Change address"}
          </Button>
          <Button
            disabled={saving}
            onClick={() => setConfirming(null)}
            variant="secondary"
          >
            Keep it
          </Button>
        </div>
      </BottomSheet>
    )}
    </>
  );
};

function SiteNameForm({
  name,
  onSave,
}: {
  name: string;
  onSave: (name: string) => Promise<string | null>;
}) {
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy || !value.trim()) return;
        setBusy(true);
        setError((await onSave(value.trim())) ?? "");
        setBusy(false);
      }}
    >
      <SectionHeading>
        <label htmlFor="website-name">Website name</label>
      </SectionHeading>
      <Input
        id="website-name"
        value={value}
        maxLength={120}
        required
        disabled={busy}
        onChange={(event) => setValue(event.target.value)}
      />
      <Hint className="mt-2">
        Appears in your site header. Publish to update your live site.
      </Hint>
      <Button
        className="mt-3"
        disabled={busy || !value.trim() || value.trim() === name}
        size="sm"
        type="submit"
        variant="secondary"
      >
        {busy ? "Saving…" : "Save name"}
      </Button>
      {error && <FieldError>{error}</FieldError>}
    </form>
  );
}

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const atLimit = used >= limit;
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium">{label}</span>
        <span className="shrink-0 tabular-nums text-hazumu-muted">
          <span className="font-semibold text-hazumu-ink">{used}</span> / {limit}
        </span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
        aria-valuetext={`${used} of ${limit} used`}
        className="h-2 overflow-hidden rounded-full bg-hazumu-border"
      >
        <div
          className={`h-full rounded-full ${atLimit ? "bg-hazumu-terracotta" : "bg-hazumu-sage"}`}
          style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
        />
      </div>
      {atLimit && <Hint className="mt-2">Allowance reached. You can still edit existing content.</Hint>}
    </div>
  );
}
